import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Button, Modal, Spinner, Typography } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';

import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { usePrivateRoute } from '@/hooks/usePrivateRoute';
import { getInvitationByIdQueryOptions } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';

export const Route = createFileRoute('/_authenticated/invite/$inviteId')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { inviteId } = useParams({
    from: '/_authenticated/invite/$inviteId',
  });
  const navigate = useNavigate();
  const { isLoading: isPrivateRouteLoading, isAuthenticated } = usePrivateRoute();
  const { setFleetAccountId } = useFleetAccount();

  const { data, isLoading: isInvitationLoading } = useQuery(
    getInvitationByIdQueryOptions({ invitationId: inviteId, enabled: isAuthenticated })
  );

  const { mutateAsync: acceptInvitation, isPending } = useMutation({
    mutationFn: (props: { inviteId: string; entityVersion: number }) =>
      vpcService.Invitations.acceptInvitation({
        invitationId: props.inviteId,
        body: {
          confirmationToken: '123456',
          entityVersion: props.entityVersion,
        },
      }),
    onSuccess: () => {
      // Invalidate user authorizations cache
      vpcService.FleetAccount.getUserAuthorizations({ clearCache: true });
      // Set the fleetID and then navigate to the dashboard
      if (!data) return;
      setFleetAccountId(data.fleetAccountId || '');
      navigate({ to: '/' });
    },
  });

  if (isPrivateRouteLoading || isInvitationLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const validInvitation = !isInvitationLoading && !!data && data.history[data.history.length - 1].status === 'Issued';

  return validInvitation ? (
    <div>
      <Modal open={true} onClose={() => navigate({ to: '/' })} width={500} maxWidth="90%">
        <span slot="header">
          {t(($) => $.components.modal.accept_invitation_modal.invite_title, {
            name: data.fleetAccountName,
          })}
        </span>
        <Typography variant="body">
          {t(($) => $.components.modal.accept_invitation_modal.invite_description, {
            fleetName: data.fleetAccountName,
          })}
        </Typography>
        <span slot="footer">
          <div className="flex gap-4">
            <Button variant="secondary" disabled={isPending} onClick={() => navigate({ to: '/' })}>
              {t(($) => $.components.modal.accept_invitation_modal.dismiss)}
            </Button>
            <Button
              variant="primary"
              disabled={isPending}
              loading={isPending}
              onClick={() => acceptInvitation({ inviteId, entityVersion: data.entityVersion })}
            >
              {t(($) => $.components.modal.accept_invitation_modal.accept)}
            </Button>
          </div>
        </span>
      </Modal>
    </div>
  ) : (
    <div className="flex flex-col items-center gap-2">
      This invitation is no longer valid.
      <Button variant="primary" onClick={() => navigate({ to: '/' })}>
        Back
      </Button>
    </div>
  );
}
